import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Store, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

type State = "capturing" | "done" | "failed";

/**
 * Landing page after PayPal approval. Finalises the capture.
 *
 * This races PayPal's webhook by design — whichever arrives first activates
 * the storefront and the other is a no-op server-side. That redundancy is
 * deliberate: the webhook covers users who close the tab before redirecting
 * back, and this page covers a webhook that is delayed or misconfigured.
 */
export default function SellerWelcome() {
  const [state, setState] = useState<State>("capturing");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const orderId = new URLSearchParams(window.location.search).get("order");
    if (!orderId) {
      setState("failed");
      setError("Missing order reference.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/api/marketplace/seller/signup/capture", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ orderId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (res.ok && data.status === "paid") {
          setState("done");
        } else {
          setState("failed");
          setError(data.error || "We could not confirm your payment.");
        }
      } catch {
        if (!cancelled) {
          setState("failed");
          setError("Network error while confirming your payment.");
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center space-y-4">
          {state === "capturing" && (
            <>
              <Loader2 className="h-10 w-10 mx-auto animate-spin text-muted-foreground" />
              <h1 className="text-xl font-bold">Confirming your payment…</h1>
              <p className="text-sm text-muted-foreground">
                Please don't close this window.
              </p>
            </>
          )}

          {state === "done" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
              <h1 className="text-xl font-bold" data-testid="text-welcome-success">
                Your storefront is live
              </h1>
              <p className="text-sm text-muted-foreground">
                You can start adding products now. Share your referral link to
                earn commission when others open a storefront.
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <Link href="/seller/products">
                  <Button className="w-full" data-testid="button-add-products">
                    <Store className="h-4 w-4 mr-2" />Add your first product
                  </Button>
                </Link>
                <Link href="/seller/earnings">
                  <Button variant="outline" className="w-full" data-testid="button-view-earnings">
                    View earnings
                  </Button>
                </Link>
              </div>
            </>
          )}

          {state === "failed" && (
            <>
              <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
              <h1 className="text-xl font-bold">We couldn't confirm payment</h1>
              <Alert variant="destructive" className="text-left">
                <AlertDescription data-testid="text-welcome-error">{error}</AlertDescription>
              </Alert>
              <p className="text-xs text-muted-foreground">
                If PayPal took payment, your storefront will activate
                automatically within a few minutes — no need to pay again.
              </p>
              <Link href="/support">
                <Button variant="outline" className="w-full">Contact support</Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
